export type Product = {
  id: number
  slug: string
  name: string
  tagline: string
  description: string
  ingredients: string
  howToUse: string
  price: number
  comparePrice: number | null
  size: string
  step: string
  category: string
  concerns: string[]
  image: string
  badge: string
  inStock: boolean
  lowStock: boolean
  featured: boolean
  rating: number | null
  reviewCount: number
}

export type Review = { author: string; location: string; rating: number; body: string; created_at?: string }
export type ProductDetail = Product & { reviews: Review[]; related: Product[] }

export type Post = {
  slug: string
  title: string
  dek: string
  category: string
  readMinutes: number
  image: string
  created_at?: string
  body?: string
}

export type Faq = { id: number; question: string; answer: string }

export type Settings = {
  storeName: string
  currency: string
  currencySymbol: string
  freeShippingThreshold: number
  standardShipping: number
  announcementBar: boolean
  announcementText: string
  countryLabel: string
  /**
   * 'card' when Stripe is configured; 'invoice' in local development, where the
   * store bills separately; 'unavailable' on a live store without Stripe, which
   * refuses checkout rather than confirm unpaid orders.
   */
  payments: 'card' | 'invoice' | 'unavailable'
  categories: string[]
  concerns: string[]
}

export type User = { id: number; email: string; name: string; role: 'admin' | 'customer' }

export type CartLine = { slug: string; name: string; image: string; unitPrice: number; qty: number; linePrice: number }
export type PricedCart = {
  items: CartLine[]
  problems: string[]
  subtotal: number
  /** Taken off by a valid ambassador code; 0 without one. */
  discount: number
  /** The code as applied (upper-case), or '' when none applied. */
  code: string
  /** Set when a code was sent but is not valid. */
  codeError: string
  shipping: number
  total: number
  currency: string
}

export type OrderSummary = {
  reference: string
  status: string
  paymentStatus: string
  createdAt: string
  customerName: string
  address: string[]
  subtotal: number
  discount: number
  discountCode: string
  shipping: number
  total: number
  currency: string
  trackingCarrier: string
  trackingNumber: string
  shippedAt: string | null
  deliveredAt: string | null
  items: { name: string; slug: string; image: string; unitPrice: number; qty: number }[]
}

/** A failed request carries the server's message and any per-field problems. */
export class ApiError extends Error {
  status: number
  fields?: { path: string; message: string }[]
  problems?: string[]

  constructor(status: number, message: string, extra?: { fields?: { path: string; message: string }[]; problems?: string[] }) {
    super(message)
    this.status = status
    this.fields = extra?.fields
    this.problems = extra?.problems
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response
  try {
    res = await fetch(`/api${path}`, {
      credentials: 'include',
      headers: init?.body instanceof FormData ? undefined : { 'Content-Type': 'application/json' },
      ...init,
    })
  } catch {
    // Network-level failure — fetch only rejects when the request never completed.
    throw new ApiError(0, 'Could not reach the server. Check your connection and try again.')
  }

  const isJson = res.headers.get('content-type')?.includes('application/json')
  const payload = isJson ? await res.json().catch(() => null) : null

  if (!res.ok) {
    throw new ApiError(res.status, payload?.error || `Request failed (${res.status}).`, {
      fields: payload?.fields,
      problems: payload?.problems,
    })
  }
  return payload as T
}

const get = <T,>(path: string) => request<T>(path)
const post = <T,>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body instanceof FormData ? body : JSON.stringify(body ?? {}) })
const put = <T,>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) })
const patch = <T,>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) })
const del = <T,>(path: string) => request<T>(path, { method: 'DELETE' })

export const api = {
  settings: () => get<Settings>('/settings'),

  products: (params: { category?: string; concern?: string; featured?: boolean; q?: string } = {}) => {
    const qs = new URLSearchParams()
    if (params.category && params.category !== 'All') qs.set('category', params.category)
    if (params.concern) qs.set('concern', params.concern)
    if (params.featured) qs.set('featured', 'true')
    if (params.q) qs.set('q', params.q)
    const s = qs.toString()
    return get<Product[]>(`/products${s ? `?${s}` : ''}`)
  },
  product: (slug: string) => get<ProductDetail>(`/products/${encodeURIComponent(slug)}`),
  featuredReviews: () => get<Review[]>('/reviews/featured'),
  submitReview: (body: { productSlug: string; author: string; location: string; rating: number; body: string }) =>
    post<{ ok: true; message: string }>('/reviews', body),

  posts: (category?: string) => get<Post[]>(`/posts${category && category !== 'All' ? `?category=${encodeURIComponent(category)}` : ''}`),
  post: (slug: string) => get<Post>(`/posts/${encodeURIComponent(slug)}`),
  faqs: () => get<Faq[]>('/faqs'),

  quizQuestions: () => get<{ questions: { t: string; o: string[] }[] }>('/quiz'),
  submitQuiz: (answers: number[], email?: string) =>
    post<{ title: string; body: string; items: Product[]; total: number }>('/quiz', { answers, email: email || '' }),

  subscribe: (email: string) => post<{ ok: true; message: string }>('/subscribe', { email }),

  priceCart: (items: { slug: string; qty: number }[], code = '') => post<PricedCart>('/cart/price', { items, code }),
  checkout: (body: Record<string, unknown>) =>
    post<{ ok: true; reference: string; checkoutUrl: string | null; total: number; currency: string; message: string }>(
      '/checkout',
      body
    ),
  order: (reference: string, email?: string) =>
    get<OrderSummary>(`/orders/${encodeURIComponent(reference)}${email ? `?email=${encodeURIComponent(email)}` : ''}`),

  me: () => get<{ user: User | null }>('/account/me'),
  login: (email: string, password: string) => post<{ user: User }>('/account/login', { email, password }),
  register: (email: string, password: string, name: string) => post<{ user: User }>('/account/register', { email, password, name }),
  logout: () => post<{ ok: true }>('/account/logout'),
  myOrders: () => get<{ reference: string; status: string; paymentStatus: string; total: number; currency: string; createdAt: string }[]>('/account/orders'),
  changePassword: (currentPassword: string, newPassword: string) =>
    post<{ ok: true; message: string }>('/account/password', { currentPassword, newPassword }),
  forgotPassword: (email: string) => post<{ ok: true; message: string }>('/account/forgot', { email }),
  resetPassword: (token: string, password: string) =>
    post<{ user: User; message: string }>('/account/reset', { token, password }),

  admin: {
    stats: () => get<AdminStats>('/admin/stats'),
    products: () => get<AdminProduct[]>('/admin/products'),
    createProduct: (b: unknown) => post<AdminProduct>('/admin/products', b),
    updateProduct: (id: number, b: unknown) => put<AdminProduct>(`/admin/products/${id}`, b),
    deleteProduct: (id: number) => del<{ ok: true; archived: boolean; message: string }>(`/admin/products/${id}`),
    setStock: (id: number, stock: number) => patch<{ ok: true }>(`/admin/products/${id}/stock`, { stock }),

    orders: (params: { status?: string; q?: string } = {}) => {
      const qs = new URLSearchParams()
      if (params.status && params.status !== 'all') qs.set('status', params.status)
      if (params.q) qs.set('q', params.q)
      const s = qs.toString()
      return get<AdminOrderRow[]>(`/admin/orders${s ? `?${s}` : ''}`)
    },
    order: (id: number) => get<AdminOrder>(`/admin/orders/${id}`),
    updateOrder: (id: number, b: unknown) => patch<{ ok: true }>(`/admin/orders/${id}`, b),

    reviews: (pendingOnly = false) => get<AdminReview[]>(`/admin/reviews${pendingOnly ? '?pending=true' : ''}`),
    updateReview: (id: number, b: unknown) => patch<{ ok: true }>(`/admin/reviews/${id}`, b),
    deleteReview: (id: number) => del<{ ok: true }>(`/admin/reviews/${id}`),

    posts: () => get<AdminPost[]>('/admin/posts'),
    createPost: (b: unknown) => post<{ ok: true }>('/admin/posts', b),
    updatePost: (id: number, b: unknown) => put<{ ok: true }>(`/admin/posts/${id}`, b),
    deletePost: (id: number) => del<{ ok: true }>(`/admin/posts/${id}`),

    faqs: () => get<Faq[]>('/admin/faqs'),
    createFaq: (b: unknown) => post<{ ok: true }>('/admin/faqs', b),
    updateFaq: (id: number, b: unknown) => put<{ ok: true }>(`/admin/faqs/${id}`, b),
    deleteFaq: (id: number) => del<{ ok: true }>(`/admin/faqs/${id}`),

    ambassadors: () => get<AdminAmbassador[]>('/admin/ambassadors'),
    ambassador: (id: number) => get<AdminAmbassadorDetail>(`/admin/ambassadors/${id}`),
    createAmbassador: (b: unknown) => post<AdminAmbassador>('/admin/ambassadors', b),
    updateAmbassador: (id: number, b: unknown) => put<AdminAmbassador>(`/admin/ambassadors/${id}`, b),
    deleteAmbassador: (id: number) => del<{ ok: true; archived: boolean; message: string }>(`/admin/ambassadors/${id}`),

    customers: () => get<AdminCustomer[]>('/admin/customers'),
    customerResetLink: (id: number) =>
      post<{ url: string; email: string; expiresInHours: number }>(`/admin/customers/${id}/reset-link`),
    subscribers: () => get<{ id: number; email: string; createdAt: string }[]>('/admin/subscribers'),
    deleteSubscriber: (id: number) => del<{ ok: true }>(`/admin/subscribers/${id}`),
    quizSubmissions: () => get<{ id: number; email: string; answers: number[]; routine: string; createdAt: string }[]>('/admin/quiz-submissions'),

    settings: () => get<Record<string, string>>('/admin/settings'),
    saveSettings: (b: Record<string, string>) => put<Record<string, string>>('/admin/settings', b),

    upload: (file: File) => {
      const fd = new FormData()
      fd.append('image', file)
      return post<{ url: string }>('/admin/upload', fd)
    },
  },
}

export type AdminStats = {
  orders: number
  pendingOrders: number
  /** Paid, not yet shipped. */
  toDispatch: number
  revenue: number
  paidRevenue: number
  customers: number
  products: number
  subscribers: number
  pendingReviews: number
  quizSubmissions: number
  byDay: { day: string; orders: number; revenue: number }[]
  topProducts: { name: string; slug: string; units: number; revenue: number }[]
  lowStock: { id: number; name: string; slug: string; stock: number; low_stock_at: number }[]
  currencySymbol: string
}

export type AdminProduct = {
  id: number
  slug: string
  name: string
  tagline: string
  description: string
  ingredients: string
  howToUse: string
  price: number
  comparePrice: number | null
  size: string
  step: string
  category: string
  concerns: string[]
  image: string
  badge: string
  stock: number
  lowStockAt: number
  active: boolean
  featured: boolean
  sortOrder: number
  updatedAt: string
}

export type AdminOrderRow = {
  id: number
  reference: string
  email: string
  customerName: string
  total: number
  currency: string
  status: string
  paymentStatus: string
  trackingNumber: string
  discountCode: string
  createdAt: string
}

export type AdminOrder = AdminOrderRow & {
  phone: string
  address: { line1: string; line2: string; city: string; postcode: string; country: string }
  subtotal: number
  discount: number
  commission: number
  shipping: number
  paymentRef: string
  trackingCarrier: string
  shippedAt: string | null
  deliveredAt: string | null
  notes: string
  updatedAt: string
  items: { name: string; slug: string; image: string; unitPrice: number; qty: number }[]
}

export type AdminReview = {
  id: number
  author: string
  location: string
  rating: number
  body: string
  approved: boolean
  featured: boolean
  productName: string | null
  productSlug: string | null
  createdAt: string
}

export type AdminPost = {
  id: number
  slug: string
  title: string
  dek: string
  body: string
  category: string
  readMinutes: number
  image: string
  published: boolean
  createdAt: string
}

export type AdminCustomer = {
  id: number
  email: string
  name: string
  orders: number
  spent: number
  createdAt: string
}

export type AdminAmbassador = {
  id: number
  name: string
  email: string
  code: string
  discountPercent: number
  commissionPercent: number
  active: boolean
  notes: string
  createdAt: string
  /** Paid orders not since cancelled or refunded. */
  orders: number
  /** Placed with the code, payment not yet taken. */
  pendingOrders: number
  revenue: number
  discountGiven: number
  commission: number
}

export type AdminAmbassadorDetail = AdminAmbassador & {
  orderList: {
    id: number
    reference: string
    customerName: string
    status: string
    paymentStatus: string
    total: number
    discount: number
    commission: number
    createdAt: string
  }[]
}

export const ORDER_STATUSES = ['pending', 'paid', 'fulfilled', 'shipped', 'delivered', 'cancelled', 'refunded'] as const
export const PAYMENT_STATUSES = ['unpaid', 'paid', 'refunded', 'failed'] as const
